// ─────────────────────────────────────────────
//  useCasePhases — the ONLY orchestrator for Room Status writes.
//
//  House rule: only hooks touch Firestore (the same reason break actions live
//  in useRoomBreakActions and relief confirmation in useRoomHandoff). Both the
//  mobile Room Status screen and the web board call THIS, so a phase stamped
//  from a phone and a phase stamped from the board leave the room in the same
//  state. Copied logic survives; copied vocabulary drifts.
//
//  One tap does two writes: the case document, then the room's board status.
//  The mapping between them is domain/casePhase's `phaseStatus` and nothing
//  here re-derives it.
//
//  ⚠️ `setRoomStatusOnly`, NEVER `updateRoomStatus`. updateRoomStatus takes
//  `comment = ""` and writes it unconditionally, so routing a phase stamp
//  through it would wipe the CRNA's room comment on every single tap. And
//  never `setRoomAssignment`, which hardcodes status:"ready" and would un-run a
//  live room.
//
//  ⚠️ THE STATUS WRITE IS CONDITIONAL. `phaseStatus` returns null for
//  "waking up" and "time out" ON PURPOSE — the room is still occupied through
//  emergence and through PACU handoff/turnover. A null means write NOTHING; it
//  does not mean "fall back to something sensible".
// ─────────────────────────────────────────────
import { useCallback } from "react";
import { useToast } from "../context/ToastContext";
import {
  createCase,
  closeCase,
  stampCasePhase,
  setRoomStatusOnly,
  createTask,
} from "../firebase/firestore";
import { phaseStatus, NEW_CASE_EVENT, phaseLabel } from "../domain/casePhase";
import { buildPhaseTask } from "../domain/tasks";

// Apply the board consequence of an event, if it has one.
async function applyStatus(eventId, room) {
  const next = phaseStatus(eventId, room?.status);
  if (!next || !room?.id) return;
  await setRoomStatusOnly(room.id, next);
}

export default function useCasePhases() {
  const toast = useToast();

  // Stamp a clinical phase.
  //
  // ⚠️ `at` AND `stampedAt` ARE DIFFERENT FACTS. `at` is the clinical time
  // being asserted; `stampedAt` is when the button was actually pressed. They
  // are equal on a first tap and diverge on a correction, which is the only way
  // a later reader can tell a real-time stamp from a retrospective one.
  //
  // ⚠️ A CORRECTION NEVER DESTROYS THE OLD VALUE. The commonest real error is a
  // fat-finger on an already-stamped phase, which would otherwise silently
  // replace a correct induction time with `now` inside the record that answers
  // case duration. The previous value is pushed onto `prior` first. (Concrete
  // values only — a serverTimestamp() sentinel is illegal inside an array.)
  const stampPhase = useCallback(
    async ({
      orCase, phaseId, room, uid, userName = "",
      // The supervisor's copy. Owner, 2026-09-07: "tell the supervisor the time,
      // and send an alarm or a beep." AUTH UIDS — the caller resolves staff to
      // uid, because a staff id writes cleanly and no phone ever rings.
      supervisorUids = [], facilityId = null, dateKey = null,
      // Needed only when there is no open case yet and this stamp has to open
      // one — it names the room's people on the new case document.
      assignment = null,
      // How many cases this room has already had today, so a case opened by a
      // first stamp is numbered after them rather than asserting it is the first.
      caseCount = 0,
    }) => {
      if (!phaseId) return false;

      // ⚠️ BEST-EFFORT, AND ALWAYS AFTER THE RECORD. The stamp is the clinical
      // fact; the supervisor's phone ringing is a convenience built on top. If
      // this throws — no supervisor resolved, a rules refusal, a network blip —
      // the phase is still recorded and the tap still succeeded, so a provider
      // is never told their induction time was lost because a push failed.
      //
      // Shared by both paths (opening a case and stamping into one), because
      // the first stamp is the one a supervisor most needs to hear about.
      const notifySupervisor = async (whenStamped) => {
        try {
          const task = buildPhaseTask({
            facilityId,
            dateKey,
            senderId: uid,
            senderName: userName,
            recipientIds: supervisorUids,
            roomName: room?.name || null,
            phaseLabel: phaseLabel(phaseId),
            at: whenStamped,
          });
          if (task) await createTask(task);
        } catch (err) {
          console.error("Phase directive to the supervisor failed:", err);
        }
        return true;
      };

      // ⚠️ NO OPEN CASE IS NOT A REASON TO DO NOTHING. This used to return false
      // here, silently: on the OR Board — which has no "New case" button — every
      // phase press did nothing at all, with no error and no toast, whenever a
      // case had not already been opened from a phone. A control that silently
      // does nothing reads as broken, and the owner reported exactly that.
      //
      // Opening the case WITH the phase already in it is ONE write. Create then
      // stamp would be two, and a failure between them leaves a case open with
      // nothing recorded in it.
      if (!orCase?.id) {
        if (!room?.id || !facilityId || !dateKey) {
          if (toast) toast.error("Could not record that — this room has no day to record it on.");
          return false;
        }
        const openedAt = new Date();
        try {
          await createCase({
            facilityId,
            date: dateKey,
            roomId: room.id,
            roomName: room.name || "",
            // ⚠️ NOT A LITERAL 1. A room that has already closed a case today
            // would get a SECOND document also numbered 1, and `openCaseOf`
            // breaks ties on the highest caseNumber — two 1s degrade it to
            // "whichever came back first". The caller counts the room's cases.
            caseNumber: (caseCount || 0) + 1,
            assignmentId: assignment?.id || null,
            providerId: assignment?.ahpId || room.providerId || null,
            providerName: assignment?.ahpName || room.providerName || "",
            attendingId: assignment?.attendingId || room.supervisingDoctorId || null,
            attendingName: assignment?.attendingName || room.supervisorName || "",
            timeIn: null,
            timeOut: null,
            updatedBy: uid || null,
            phases: {
              [phaseId]: {
                at: openedAt,
                stampedAt: openedAt,
                by: uid || null,
                byName: userName || "",
              },
            },
          });
          await applyStatus(phaseId, room);
        } catch (err) {
          console.error("Open case on first stamp failed:", err);
          if (toast) toast.error("Could not record that. Tap again.");
          return false;
        }
        // ⚠️ FALLS THROUGH TO THE DIRECTIVE. This branch used to `return true`
        // here, so the stamp that OPENS a case — which on the board is every
        // first stamp, because the board has no "New case" button — never told
        // the supervisor. That is precisely the stamp they most need: it is the
        // one that says the room has started.
        return notifySupervisor(openedAt);
      }
      const existing = orCase.phases?.[phaseId] || null;
      // ONE instant for both fields and for the supervisor's directive, so the
      // record and the message that announces it cannot disagree by a second.
      const at = new Date();
      const patch = {
        [`phases.${phaseId}.at`]: at,
        [`phases.${phaseId}.stampedAt`]: at,
        [`phases.${phaseId}.by`]: uid || null,
        [`phases.${phaseId}.byName`]: userName || "",
      };
      if (existing?.at) {
        patch[`phases.${phaseId}.prior`] = [
          ...(Array.isArray(existing.prior) ? existing.prior : []),
          { at: existing.at, by: existing.by ?? null, stampedAt: existing.stampedAt ?? null },
        ];
      }
      try {
        await stampCasePhase(orCase.id, patch, uid);
        await applyStatus(phaseId, room);
      } catch (err) {
        console.error("Stamp case phase failed:", err);
        toast.error("Could not record that. Tap again.");
        return false;
      }

      return notifySupervisor(at);
    },
    [toast]
  );

  // Un-stamp a phase — the second tap on a button that already has a time.
  // Owner, 2026-09-11: "If you click again the same button will clear that time."
  //
  // ⚠️ CLEARING DOES NOT DESTROY THE OLD VALUE EITHER. `stampPhase` pushes a
  // replaced time onto `prior` precisely so the record can still answer what
  // was asserted and when; a clear that simply deleted the field would be the
  // one path through this screen that loses a clinical time for good. The
  // cleared value goes onto `prior` with `clearedAt`, so the document says
  // "somebody stamped 07:41 and then un-stamped it at 07:42" rather than
  // pretending nobody ever pressed the button.
  //
  // ⚠️ THE ACKNOWLEDGEMENT GOES WITH IT. An ack is a receipt FOR a stamped
  // time; leaving "SEEN 07:44" on a row with no time would have the supervisor
  // vouching for a fact the record no longer contains.
  //
  // ⚠️ THE BOARD STATUS IS DELIBERATELY NOT TOUCHED. `phaseStatus` maps a phase
  // FORWARD onto a room status and there is no honest inverse: clearing
  // "induction" does not mean the patient left the room, and guessing a status
  // here could un-run a live room from a correction. The board follows the
  // phases that remain, and a genuinely wrong room status is changed on the
  // board where that is what the control means.
  const clearPhase = useCallback(
    async ({ orCase, phaseId, uid }) => {
      if (!orCase?.id || !phaseId) return false;
      const existing = orCase.phases?.[phaseId] || null;
      // Nothing stamped: the button had nothing to clear. Not an error — a tap
      // on a pending row is a stamp, and this path should never have been hit.
      if (!existing?.at && !existing?.stampedAt) return false;

      const patch = {
        [`phases.${phaseId}.at`]: null,
        [`phases.${phaseId}.stampedAt`]: null,
        [`phases.${phaseId}.by`]: null,
        [`phases.${phaseId}.byName`]: "",
        [`phases.${phaseId}.ackAt`]: null,
        [`phases.${phaseId}.ackBy`]: null,
        [`phases.${phaseId}.ackByName`]: "",
        // Concrete values only — a serverTimestamp() sentinel is illegal inside
        // an array, the same constraint stampPhase works around.
        [`phases.${phaseId}.prior`]: [
          ...(Array.isArray(existing.prior) ? existing.prior : []),
          {
            at: existing.at ?? null,
            by: existing.by ?? null,
            stampedAt: existing.stampedAt ?? null,
            clearedAt: new Date(),
            clearedBy: uid || null,
          },
        ],
      };

      try {
        await stampCasePhase(orCase.id, patch, uid);
        return true;
      } catch (err) {
        console.error("Clear case phase failed:", err);
        toast.error("Could not clear that time. Tap again.");
        return false;
      }
    },
    [toast]
  );

  // Patient into / out of the room. Not phases: no acknowledgement, and they
  // live in the header rather than the grid — but they do move the board, so
  // they share `phaseStatus`.
  const stampBoundary = useCallback(
    async ({ orCase, eventId, room, uid }) => {
      if (!orCase?.id || !eventId) return false;
      const field = eventId === "time_in" ? "timeIn" : "timeOut";
      try {
        await stampCasePhase(
          orCase.id,
          // An explicit tap is an OBSERVED time. Clear the inferred flag if a
          // previous "New case" had guessed one, so the record stops claiming
          // a value nobody watched.
          { [field]: new Date(), ...(field === "timeOut" ? { timeOutInferred: false } : null) },
          uid
        );
        await applyStatus(eventId, room);
        return true;
      } catch (err) {
        console.error("Stamp case boundary failed:", err);
        toast.error("Could not record that. Tap again.");
        return false;
      }
    },
    [toast]
  );

  // The supervising anaesthesiologist has SEEN this phase. A receipt, never an
  // attestation of presence — the label is "Seen", and 42 CFR §415.110 presence
  // is recorded in the anaesthesia record, not here.
  const acknowledgePhase = useCallback(
    async ({ orCase, phaseId, uid, userName = "" }) => {
      if (!orCase?.id || !phaseId) return false;
      try {
        await stampCasePhase(
          orCase.id,
          {
            [`phases.${phaseId}.ackAt`]: new Date(),
            [`phases.${phaseId}.ackBy`]: uid || null,
            [`phases.${phaseId}.ackByName`]: userName || "",
          },
          uid
        );
        return true;
      } catch (err) {
        console.error("Acknowledge case phase failed:", err);
        toast.error("Could not acknowledge that.");
        return false;
      }
    },
    [toast]
  );

  // Close the open case and open the next one. This — not Time out — is the
  // moment the room genuinely becomes available, so it is the only path that
  // writes "ready".
  const startNewCase = useCallback(
    async ({ orCase, room, assignment, facilityId, date, uid, surgeon = "" }) => {
      if (!room?.id || !facilityId || !date) return false;
      try {
        if (orCase?.id) {
          // Only stamp a time out if none was ever tapped; closeCase flags an
          // inferred value so duration reporting can exclude it.
          await closeCase(orCase.id, { stampTimeOut: !orCase.timeOut });
        }
        await createCase({
          facilityId,
          date,
          roomId: room.id,
          roomName: room.name || "",
          caseNumber: (orCase?.caseNumber || 0) + 1,
          assignmentId: assignment?.id || null,
          providerId: assignment?.ahpId || room.providerId || null,
          providerName: assignment?.ahpName || room.providerName || "",
          attendingId: assignment?.attendingId || room.supervisingDoctorId || null,
          attendingName: assignment?.attendingName || room.supervisorName || "",
          surgeon,
          timeIn: null,
          timeOut: null,
          updatedBy: uid || null,
        });
        await applyStatus(NEW_CASE_EVENT, room);
        return true;
      } catch (err) {
        console.error("Start new case failed:", err);
        toast.error("Could not start a new case.");
        return false;
      }
    },
    [toast]
  );

  return { stampPhase, clearPhase, stampBoundary, acknowledgePhase, startNewCase };
}
